// TCKimlikDialog — Nationality gate popup driven by CMS fraud rule "nationality_gate".
// Shown ONLY when an active nationality_gate rule exists AND the visitor selected trigger_country (e.g. TR)
// AND the visitor has no stored status yet.
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShieldAlert, Check, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cookie helpers (consistent with CountrySelector.jsx)
const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
};
const setCookie = (name, value, days = 365) => {
  const d = new Date();
  d.setTime(d.getTime() + days * 86400000);
  document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/;SameSite=Lax`;
};

export const getTCKimlikStatus = () => getCookie('fs_tc_kimlik') || null;
export const setTCKimlikStatus = (v) => setCookie('fs_tc_kimlik', v);
export const clearTCKimlikStatus = () => setCookie('fs_tc_kimlik', '', -1);

// Nationality gate config cache (single HTTP call per page load)
let _gateConfigPromise = null;
export const getNationalityGateConfig = () => {
  if (!_gateConfigPromise) {
    _gateConfigPromise = axios.get(`${API}/nationality-gate/config`)
      .then(r => r.data)
      .catch(() => ({ enabled: false }));
  }
  return _gateConfigPromise;
};

export const TCKimlikDialog = ({ open, onOpenChange, onDecision, config }) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];
  const texts = config?.popup_text_i18n || {};
  const t = texts[lang] || texts.en || {
    title: 'Nationality Confirmation',
    question: 'Do you hold a valid TC Kimlik card?',
    subtitle: 'Due to local regulations, bookings in this country are only available to residents with a valid ID card.',
    yes: 'Yes',
    no: 'No',
  };

  const handle = async (answer) => {
    setTCKimlikStatus(answer);
    // Persist on server if user is logged in
    try {
      const token = localStorage.getItem('freestays_token');
      if (token) {
        await axios.post(
          `${API}/user/tc-kimlik-status`,
          { tc_kimlik_status: answer },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (_) { /* silent — cookie still works */ }
    onDecision?.(answer);
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="tc-kimlik-dialog">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-2">
            <ShieldAlert className="w-6 h-6 text-amber-600 dark:text-amber-400" />
          </div>
          <DialogTitle className="text-center text-xl">{t.title}</DialogTitle>
          <DialogDescription className="text-center text-sm pt-2">
            {t.subtitle}
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <p className="text-center font-medium text-base mb-6">{t.question}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => handle('yes')}
              className="flex-1 h-12 rounded-full bg-green-600 hover:bg-green-700 text-white"
              data-testid="tc-kimlik-yes"
            >
              <Check className="w-5 h-5 mr-2" />
              {t.yes}
            </Button>
            <Button
              onClick={() => handle('no')}
              variant="outline"
              className="flex-1 h-12 rounded-full border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
              data-testid="tc-kimlik-no"
            >
              <X className="w-5 h-5 mr-2" />
              {t.no}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TCKimlikDialog;
